import * as React from 'react';

import * as UI from 'core/ui';
import * as Images from 'pos/Images';

import { PosScreen, Header, Content, Footer, PhoneOnly, BackActionButton, Filler, AcceptActionButton, LargeDeviceOnly } from 'pos/ui';
import { ProductForSaleDTO, Res, Permission } from 'core/api';
import { Result } from 'core/infrastructure';
import { Preconditions } from 'core/infrastructure-next';
import { Message, Modal } from 'core/ui-next/modal';
import { VStack, Text, Span, Box } from '@chakra-ui/react';
import { BasicButton, SvgIcon } from 'core/ui-next';
import { FormRef, useControlsFor } from 'core/ui-next/controls';
import { z } from 'zod';
import { zDecimalToString } from 'core/infrastructure-next';

type Props = {
	title: Nullable<string>;
	productName: string;
} & Modal.Actions<number>;

const stateSchema = z.object({
	initialAmount: zDecimalToString({ nonNegative: true, required: true }),
});

type State = z.infer<typeof stateSchema>;

function EditPrice(props: Props) {

	const { title, productName, cancel, close } = props;
	
	const formRef = React.useRef<FormRef<State>>(null);
	const { Form, DecimalInput } = useControlsFor<State>();

	const onAccept = async () => {
		const result = await formRef.current!.getValue();
		if (!result.ok)
			return;

		const isValidPrice = result.value.initialAmount !== null && parseFloat(result.value.initialAmount) <= 9999999.99;
		if (!isValidPrice) {
			await Message.show(Res.Msg.MSG_INVALID_PRICE_VALUE());
			return;
		}

		close(parseFloat(result.value.initialAmount));
	};

	const onCancel = React.useCallback(async () => cancel(), [cancel]);

	return <PosScreen screenId='EDIT_PRICE' size='small' useKeyPad={true}>
		<Header title={title || Res.Web.EDIT_TICKET_LINE_PRICE()} onClose={cancel} />
		<Content>
			<VStack paddingX={'6'} paddingY={'9'} h='full' w='full' gap={'6'}>
				<VStack gap={'4'} justifyItems={'center'} w='full'>
					<SvgIcon src={Images.EditTicketLine} w={'12'} h={'12'} />
					<VStack gap={'2'} justifyItems={'center'} w='full' textAlign={'center'}>
						<Text fontSize={'md'} fontWeight={'bold'}>
							{Res.Web.EDIT_TICKET_LINE_NEW_PRODUCT_PRICE_LBL()}
						</Text>
						<Span fontSize={'sm'} color={{ base: 'neutral.500', _dark: 'neutral.400' }}>{productName}</Span>
					</VStack>
				</VStack>
				<VStack w={'full'} justifyContent={'center'}>
					<Box w={'full'}>
						<Form ref={formRef} initialValue={{ initialAmount: '' }} schema={stateSchema}>
							<DecimalInput 
								linkTo='initialAmount'
								placeholder={Res.Web.EDIT_TICKET_LINE_PRICE_LBL()}
								onKeyDown={UI.whenEnterKey(onAccept)}
							/>
						</Form>
					</Box>
				</VStack>
				<LargeDeviceOnly>
					<BasicButton type='success' text={Res.Common.OK_BTN()} onClick={onAccept} />
				</LargeDeviceOnly>	
			</VStack>
		</Content>
		<Footer>
			<PhoneOnly>
				<BackActionButton action={onCancel} />
				<Filler />
				<AcceptActionButton action={onAccept} />
			</PhoneOnly>
		</Footer>
	</PosScreen>;
}

export async function getPrice(product: ProductForSaleDTO): Promise<Result<number>> {

	if (product.HasFixedPrice)
		return Result.ok(product.Price!);

	if (!Preconditions.ensure(Permission.Ticket_SetUnusualProductPrice)()) {
		return Result.error();
	}

	const result = await Modal.show(EditPrice, { title: Res.Web.OBTAIN_PRODUCT_PRICE(), productName: product.Name });
	return result.ok ? Result.ok(result.value) : Result.error();
}